import {
  Button,
  CountryInput,
  DatePicker,
  FieldWrapper,
  Input,
  InputErrorMessage,
  Label,
  Sheet,
  SheetContent,
  SheetTitle,
} from '@commenda/kakashi';
import { Country } from '@prisma/client';
import { useState } from 'react';
import { SUBMIT_VALIDATION_TOAST } from './submitToast';

export type NewPersonDraft = {
  firstName: string;
  lastName?: string;
  dateOfBirth: string;
  nationalities: Country[];
};

interface AddPersonBottomsheetProps {
  isOpen: boolean;
  onClose: () => void;
  // Stage-only: hands the draft back to the parent form. The Director is
  // created atomically on the parent's submit so abandoning the form leaves
  // no orphan rows.
  onStaged: (person: NewPersonDraft) => void;
  onError?: (message: string) => void;
}

export const AddPersonBottomsheet = ({
  isOpen,
  onClose,
  onStaged,
  onError,
}: AddPersonBottomsheetProps) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [nationalities, setNationalities] = useState<Country[]>([]);
  const [errors, setErrors] = useState<{
    firstName?: string;
    dateOfBirth?: string;
    nationalities?: string;
  }>({});

  const reset = () => {
    setFirstName('');
    setLastName('');
    setDateOfBirth('');
    setNationalities([]);
    setErrors({});
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Nested form: stop the parent shareholder form from also submitting.
    e.stopPropagation();

    const next: typeof errors = {};
    if (!firstName.trim()) next.firstName = 'First name is required.';
    if (!dateOfBirth) next.dateOfBirth = 'Date of birth is required.';
    if (nationalities.length === 0)
      next.nationalities = 'Select at least one nationality.';
    setErrors(next);
    if (Object.keys(next).length > 0) {
      onError?.(SUBMIT_VALIDATION_TOAST);
      return;
    }

    onStaged({
      firstName: firstName.trim(),
      lastName: lastName.trim() || undefined,
      dateOfBirth,
      nationalities,
    });
    handleClose();
  };

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <SheetContent side="bottom" preventCloseOnOutsideClick hideCloseIcon>
        {/* Scroll the body, not the panel, so the drag handle stays pinned/reachable. */}
        <div className="flex-1 min-h-0 w-full overflow-y-auto overscroll-contain flex flex-col items-center px-10 pt-5 pb-12">
          <form
            onSubmit={handleSubmit}
            className="w-full max-w-xl flex flex-col"
          >
            <div className="w-full flex flex-col gap-8">
              <SheetTitle className="w-full text-2xl leading-8">
                Add person
              </SheetTitle>

              <div className="w-full bg-bg-primary border border-border-secondary rounded-lg">
                <div className="flex flex-col gap-xl p-6">
                  <div className="grid grid-cols-2 gap-2xl">
                    <FieldWrapper>
                      <Label htmlFor="addPersonFirstName" required>
                        First name
                      </Label>
                      <Input
                        id="addPersonFirstName"
                        placeholder="John"
                        value={firstName}
                        onChange={(e) => {
                          setFirstName(e.target.value);
                          if (errors.firstName)
                            setErrors((p) => ({ ...p, firstName: undefined }));
                        }}
                      />
                      {errors.firstName && (
                        <InputErrorMessage message={errors.firstName} />
                      )}
                    </FieldWrapper>

                    <FieldWrapper>
                      <Label htmlFor="addPersonLastName">
                        Last name (optional)
                      </Label>
                      <Input
                        id="addPersonLastName"
                        placeholder="Smith"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                      />
                    </FieldWrapper>
                  </div>

                  <FieldWrapper>
                    <Label htmlFor="addPersonDateOfBirth" required>
                      Date of birth
                    </Label>
                    <DatePicker
                      setDate={(dt) => {
                        setDateOfBirth(dt ?? '');
                        if (errors.dateOfBirth)
                          setErrors((p) => ({
                            ...p,
                            dateOfBirth: undefined,
                          }));
                      }}
                      date={dateOfBirth}
                      hasError={!!errors.dateOfBirth}
                      disabled={{ after: new Date() }}
                    />
                    {errors.dateOfBirth && (
                      <InputErrorMessage message={errors.dateOfBirth} />
                    )}
                  </FieldWrapper>

                  <FieldWrapper>
                    <Label required>Nationalities</Label>
                    <CountryInput
                      type="multiple"
                      value={nationalities}
                      onValueChange={(v) => {
                        setNationalities(v as Country[]);
                        if (errors.nationalities)
                          setErrors((p) => ({ ...p, nationalities: undefined }));
                      }}
                      placeholder="Select countries"
                      error={errors.nationalities}
                    />
                  </FieldWrapper>
                </div>
              </div>
            </div>

            <div className="w-full flex items-center justify-end gap-4 py-4">
              <Button
                variant="secondary"
                size="md"
                type="button"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button variant="primary" size="md" type="submit">
                Add person
              </Button>
            </div>
          </form>
        </div>
      </SheetContent>
    </Sheet>
  );
};
